import {
  copyDiscussions,
  createBulkDiscussions,
  createDiscussion,
  deleteDiscussion,
  fetchDiscussionById,
  fetchDiscussions,
  followTrack,
  getTrackFollow,
  toggleDiscussionReaction,
  updateDiscussion,
} from '@disco/api/discussions'
import type {
  BulkCreateDiscussionRequest,
  BulkCreateDiscussionResponse,
  CopyDiscussionsRequest,
  CopyDiscussionsResponse,
  CreateDiscussionRequest,
  Discussion,
  DiscussionId,
  DiscussionUser,
  FollowTrackRequest,
  FollowTrackResponse,
  ListDiscussionsParams,
  PaginatedDiscussions,
  ReactDiscussionResponse,
  TrackFollowState,
  UpdateDiscussionRequest,
} from 'types/discussion'
import type { State } from 'types/redux'
import type { User } from 'types/user'

import { api } from './index'

const discussionListTagId = (
  target_type: string,
  target_object_id: number | string,
): string => `LIST-${target_type}-${target_object_id}`

/**
 * Builds the minimal `DiscussionUser` shape the BE returns on each record
 * from whatever's in `state.user`. Used to populate `reactions[].user`
 * during optimistic patches.
 */
const buildCurrentDiscussionUser = (state: State): DiscussionUser | null => {
  const user: User | undefined = state.user
  if (!user || user.id == null) return null
  return {
    id: user.id,
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
  }
}

/**
 * Locates and patches a discussion inside a cached list response.
 * Based off maximum 1 level deep reply structure.
 */
export const patchDiscussionInList = (
  results: Discussion[],
  id: DiscussionId,
  parent: DiscussionId | null,
  patch: (draft: Discussion) => void,
): void => {
  if (parent === null) {
    const target = results.find(d => d.id === id)
    if (target) patch(target)
    return
  }
  const top = results.find(d => d.id === parent)
  const reply = top?.replies?.find(r => r.id === id)
  if (reply) patch(reply)
}

export const discussionsApi = api
  .enhanceEndpoints({ addTagTypes: ['Discussions'] })
  .injectEndpoints({
    endpoints: build => ({
      getDiscussions: build.query<PaginatedDiscussions, ListDiscussionsParams>({
        queryFn: async params => {
          try {
            const data = await fetchDiscussions(params)
            return { data }
          } catch (error) {
            return { error }
          }
        },
        providesTags: (_result, _error, { target_type, target_object_id }) => [
          {
            type: 'Discussions',
            id: discussionListTagId(target_type, target_object_id),
          },
        ],
      }),

      getDiscussionById: build.query<Discussion, DiscussionId>({
        queryFn: async id => {
          try {
            const data = await fetchDiscussionById(id)
            return { data }
          } catch (error) {
            return { error }
          }
        },
        providesTags: (_result, _error, id) => [{ type: 'Discussions', id }],
      }),

      createDiscussion: build.mutation<Discussion, CreateDiscussionRequest>({
        queryFn: async data => {
          try {
            const result = await createDiscussion(data)
            return { data: result }
          } catch (error) {
            return { error }
          }
        },
        invalidatesTags: (
          _result,
          _error,
          { target_type, target_object_id, parent },
        ) => {
          const tags: { type: 'Discussions'; id: string | number }[] = [
            {
              type: 'Discussions',
              id: discussionListTagId(target_type, target_object_id),
            },
          ]

          if (parent != null) tags.push({ type: 'Discussions', id: parent })
          return tags
        },
      }),

      getTrackFollow: build.query<
        TrackFollowState,
        FollowTrackRequest['target_object_id']
      >({
        queryFn: async trackId => {
          try {
            return { data: await getTrackFollow(trackId) }
          } catch (error) {
            return { error }
          }
        },
      }),

      followTrack: build.mutation<FollowTrackResponse, FollowTrackRequest>({
        queryFn: async data => {
          try {
            const result = await followTrack(data)
            return { data: result }
          } catch (error) {
            return { error }
          }
        },
      }),

      createBulkDiscussions: build.mutation<
        BulkCreateDiscussionResponse,
        BulkCreateDiscussionRequest
      >({
        queryFn: async data => {
          try {
            const result = await createBulkDiscussions(data)
            return { data: result }
          } catch (error) {
            return { error }
          }
        },
        invalidatesTags: (_result, _error, { targets }) =>
          targets.map(({ target_type, target_object_id }) => ({
            type: 'Discussions' as const,
            id: discussionListTagId(target_type, target_object_id),
          })),
      }),

      copyDiscussions: build.mutation<
        CopyDiscussionsResponse,
        CopyDiscussionsRequest
      >({
        queryFn: async data => {
          try {
            const result = await copyDiscussions(data)
            return { data: result }
          } catch (error) {
            return { error }
          }
        },
        invalidatesTags: (_result, _error, { targets }) =>
          targets.map(({ target_type, target_object_id }) => ({
            type: 'Discussions' as const,
            id: discussionListTagId(target_type, target_object_id),
          })),
      }),

      updateDiscussion: build.mutation<
        Discussion,
        {
          id: DiscussionId
          data: UpdateDiscussionRequest
          target_type: string
          target_object_id: number | string
        }
      >({
        queryFn: async ({ id, data }) => {
          try {
            const result = await updateDiscussion(id, data)
            return { data: result }
          } catch (error) {
            return { error }
          }
        },
        invalidatesTags: (
          _result,
          _error,
          { id, target_type, target_object_id },
        ) => [
          { type: 'Discussions', id },
          {
            type: 'Discussions',
            id: discussionListTagId(target_type, target_object_id),
          },
        ],
      }),

      deleteDiscussion: build.mutation<
        void,
        {
          id: DiscussionId
          parent: DiscussionId | null
          target_type: string
          target_object_id: number | string
        }
      >({
        queryFn: async ({ id }) => {
          try {
            await deleteDiscussion(id)
            return { data: undefined }
          } catch (error) {
            return { error }
          }
        },
        invalidatesTags: (_result, _error, { id }) => [
          { type: 'Discussions', id },
        ],
        async onQueryStarted(
          { id, parent, target_type, target_object_id },
          { dispatch, getState, queryFulfilled },
        ) {
          const cachedListQueries = discussionsApi.util
            .selectInvalidatedBy(getState(), [
              {
                type: 'Discussions',
                id: discussionListTagId(target_type, target_object_id),
              },
            ])
            .map(entry => entry.originalArgs as ListDiscussionsParams)

          const patchResults = [
            ...(parent != null
              ? [
                  dispatch(
                    discussionsApi.util.updateQueryData(
                      'getDiscussionById',
                      parent,
                      draft => {
                        if (draft.replies) {
                          draft.replies = draft.replies.filter(r => r.id !== id)
                        }
                        if (draft.replies_count > 0) draft.replies_count -= 1
                      },
                    ),
                  ),
                ]
              : []),
            ...cachedListQueries.map(originalArgs =>
              dispatch(
                discussionsApi.util.updateQueryData(
                  'getDiscussions',
                  originalArgs,
                  draft => {
                    if (parent == null) {
                      draft.results = draft.results.filter(d => d.id !== id)
                      return
                    }
                    const top = draft.results.find(d => d.id === parent)
                    if (!top) return
                    if (top.replies) {
                      top.replies = top.replies.filter(r => r.id !== id)
                    }
                    if (top.replies_count > 0) top.replies_count -= 1
                  },
                ),
              ),
            ),
          ]

          try {
            await queryFulfilled
          } catch {
            patchResults.forEach(p => p.undo())
          }
        },
      }),

      toggleDiscussionReaction: build.mutation<
        ReactDiscussionResponse,
        {
          id: DiscussionId
          parent: DiscussionId | null
          target_type: string
          target_object_id: number | string
          type: string
        }
      >({
        queryFn: async ({ id, type }) => {
          try {
            const data = await toggleDiscussionReaction(id, { type })
            return { data }
          } catch (error) {
            return { error }
          }
        },
        async onQueryStarted(
          { id, parent, target_type, target_object_id, type },
          { dispatch, getState, queryFulfilled },
        ) {
          // Optimistically toggle the current user reaction

          const currentUser = buildCurrentDiscussionUser(getState())
          if (!currentUser) {
            try {
              await queryFulfilled
            } catch {
              // No optimistic patch to undo.
            }
            return
          }

          const togglePatch = (draft: Discussion) => {
            const idx = draft.reactions.findIndex(
              r => r.type === type && r.user.id === currentUser.id,
            )
            if (idx >= 0) {
              draft.reactions.splice(idx, 1)
            } else {
              draft.reactions.push({ type, user: currentUser })
            }
          }

          const cachedListQueries = discussionsApi.util
            .selectInvalidatedBy(getState(), [
              {
                type: 'Discussions',
                id: discussionListTagId(target_type, target_object_id),
              },
            ])
            .map(entry => entry.originalArgs as ListDiscussionsParams)

          const patchResults = [
            dispatch(
              discussionsApi.util.updateQueryData(
                'getDiscussionById',
                id,
                draft => {
                  togglePatch(draft)
                },
              ),
            ),
            ...cachedListQueries.map(originalArgs =>
              dispatch(
                discussionsApi.util.updateQueryData(
                  'getDiscussions',
                  originalArgs,
                  draft => {
                    patchDiscussionInList(
                      draft.results,
                      id,
                      parent,
                      togglePatch,
                    )
                  },
                ),
              ),
            ),
          ]

          try {
            await queryFulfilled
          } catch {
            patchResults.forEach(p => p.undo())
          }
        },
      }),
    }),
  })

export const {
  useGetDiscussionsQuery,
  useGetDiscussionByIdQuery,
  useCreateDiscussionMutation,
  useCreateBulkDiscussionsMutation,
  useCopyDiscussionsMutation,
  useUpdateDiscussionMutation,
  useDeleteDiscussionMutation,
  useToggleDiscussionReactionMutation,
  useFollowTrackMutation,
  useGetTrackFollowQuery,
} = discussionsApi
